import AlertTriangleIcon from '@assets/icons/alert-triangle.svg';
import i18n from '@/utils/i18n';
import React, { Ref, useCallback, useMemo } from 'react';
import {
  BORDER_RADIUS_SMALL,
  LIST_GAP_HORIZONTAL,
  LIST_ITEM_PADDING,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING_XS,
  TYPOGRAPHY,
} from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { FileTag } from '@/components/file-tag';
import {
  FlatList,
  RefreshControl,
  ScrollViewProps,
  Text,
  View,
} from 'react-native';
import { Props as ListItemProps } from '@/components/list-item';
import {
  ListSectionHeader,
  RecyclerList,
  Props as RecyclerListProps,
  RecyclerListRef,
} from '@/components/recycler-list';
import {
  OrderInboundOption,
  OrderInboundOptionHeader,
  OrderInboundSection,
} from './types';
import { RenderTarget } from '@shopify/flash-list';
import { Skeleton } from 'react-native-skeletons';
import { useIsSmallWebLayout } from '@/screens/order-inbounds/hooks/use-is-small-web-layout';

interface Props {
  isLoading?: boolean;
  isMergeList?: boolean;
  isRefreshing?: boolean;
  ListEmptyComponent?: React.ReactElement | null;
  ListFooterComponent?: React.ReactElement | null;
  ListHeaderComponent?: React.ReactNode;
  listItemProps?: Partial<ListItemProps<OrderInboundOption>>;
  listRef: Ref<RecyclerListRef<OrderInboundOption>>;
  onEndReached?: () => void;
  onRefresh?: () => void;
  onSelect: RecyclerListProps<OrderInboundOption>['onSelect'];
  renderListHeaderAsListItem?: boolean;
  renderSectionHeader?: (
    item: ListSectionHeader<OrderInboundOptionHeader>,
    target: RenderTarget,
    index: number,
  ) => React.ReactElement | null;
  scrollViewProps?: ScrollViewProps;
  sections: OrderInboundSection[];
  selectedOption?: OrderInboundOption | null;
}

export const ORDER_INBOUND_LIST_ITEM_HEIGHT = 72;
export const ORDER_INBOUND_LIST_ITEM_TIME_WIDTH = 80;
export const ORDER_INBOUND_LIST_ITEM_MARGIN = 16;

export const OrderInboundsList: React.FC<Props> = ({
  isLoading,
  ListEmptyComponent,
  ListFooterComponent,
  ListHeaderComponent,
  listItemProps: overrideListItemProps,
  onEndReached,
  onSelect = () => undefined,
  onRefresh,
  isRefreshing,
  renderListHeaderAsListItem,
  renderSectionHeader,
  scrollViewProps,
  sections,
  selectedOption,
  listRef,
  isMergeList,
}) => {
  const [styles, theme] = useStyles();
  const isSmallWebLayout = useIsSmallWebLayout();

  const isOptionSelected = useCallback(
    (option: OrderInboundOption) => option.value === selectedOption?.value,
    [selectedOption],
  );

  const listItemProps: Partial<ListItemProps<OrderInboundOption>> = useMemo(
    () =>
      isLoading
        ? {
            listItemStyle: styles.listItem,
            renderMainContent: () => (
              <View style={styles.flex}>
                <Skeleton count={2} height={18} spacing={4} />
              </View>
            ),
          }
        : {
            listItemHoveredStyle: isSmallWebLayout
              ? null
              : styles.listItemHovered,
            listItemStyle: (option) => [
              styles.listItem,
              isOptionSelected(option) ? styles.listItemSelected : null,
            ],
            optionListItem: styles.optionListItem,
            renderMainContent: (option, isHovered) => {
              const hasUnknownContact = option.metadata?.hasUnknownContact;
              const subtitle = option.subtitle ? (
                <Text
                  numberOfLines={1}
                  style={[TYPOGRAPHY.listSubtitle, styles.listItemSubtitle]}
                >
                  {hasUnknownContact && isMergeList
                    ? i18n.t('unknown_contact.subtitle')
                    : option.subtitle}
                </Text>
              ) : null;
              const secondaryTitle = hasUnknownContact ? (
                <View style={styles.secondaryTitleWrapper}>
                  <AlertTriangleIcon
                    height={16}
                    stroke={theme.sk.color.icon.warning.secondary}
                    width={16}
                  />
                  {subtitle}
                </View>
              ) : (
                subtitle
              );
              return (
                <View style={styles.listContentWrapper}>
                  <View style={styles.listContent}>
                    <View style={styles.listContentTitles}>
                      <Text
                        numberOfLines={1}
                        style={[TYPOGRAPHY.listTitle, styles.listItemTitle]}
                      >
                        {option.title}
                      </Text>
                      {secondaryTitle}
                    </View>
                    <Text
                      style={[
                        TYPOGRAPHY.listSubtitle,
                        styles.listItemSubtitle,
                        styles.timeText,
                      ]}
                    >
                      {option.metadata?.time}
                    </Text>
                  </View>
                  {option.metadata?.tags.length ? (
                    <FlatList
                      contentContainerStyle={styles.tags}
                      data={option.metadata!.tags}
                      horizontal
                      keyExtractor={({ text }, i) => `${text}-${i}`}
                      renderItem={({ item: { iconType, text } }) => (
                        <FileTag
                          iconType={iconType}
                          isHovered={isHovered && !isSmallWebLayout}
                          isSelected={isOptionSelected(option)}
                        >
                          {text}
                        </FileTag>
                      )}
                      showsHorizontalScrollIndicator={false}
                      style={
                        isSmallWebLayout
                          ? styles.tagsWrapperNarrow
                          : styles.tagsWrapper
                      }
                      testID="order-inbounds-list-tags"
                    />
                  ) : null}
                </View>
              );
            },
            ...overrideListItemProps,
          },
    [
      isLoading,
      isMergeList,
      isOptionSelected,
      isSmallWebLayout,
      overrideListItemProps,
      styles,
      theme,
    ],
  );

  return (
    <RecyclerList
      {...scrollViewProps}
      contentContainerStyle={styles.base}
      estimatedItemSize={100}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={ListHeaderComponent}
      listItemProps={listItemProps}
      listRef={listRef}
      onEndReached={onEndReached}
      onSelect={onSelect}
      refreshControl={
        onRefresh ? (
          <RefreshControl
            onRefresh={onRefresh}
            progressViewOffset={0}
            refreshing={Boolean(isRefreshing)}
          />
        ) : undefined
      }
      renderListHeaderAsListItem={renderListHeaderAsListItem}
      renderSectionHeader={
        isLoading
          ? () => (
              <View style={[styles.skeletonHeader, styles.flex]}>
                <Skeleton count={1} height={18} />
              </View>
            )
          : renderSectionHeader
      }
      sections={sections}
      shouldRenderSeparator={() => false}
      skipInteractionCheck
      testID="order-inbounds-list"
    />
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    paddingBottom: LIST_ITEM_PADDING,
    paddingTop: 0,
  },
  flex: {
    flex: 1,
  },
  listContent: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
    gap: LIST_GAP_HORIZONTAL,
    justifyContent: 'space-between',
  },
  listContentTitles: {
    flexGrow: 1,
    flexShrink: 1,
    gap: 4,
  },
  listContentWrapper: {
    flex: 1,
    gap: 8,
  },
  listItem: {
    borderBottomWidth: 0,
    borderRadius: BORDER_RADIUS_SMALL,
    marginHorizontal: SCREEN_PADDING_XS,
    overflow: 'hidden',
    paddingHorizontal: ORDER_INBOUND_LIST_ITEM_MARGIN,
    paddingVertical: ORDER_INBOUND_LIST_ITEM_MARGIN + SCREEN_PADDING_XS / 2,
  },
  listItemHovered: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
  },
  listItemSelected: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  listItemSubtitle: {
    color: theme.colors.inactiveTextPMC,
    lineHeight: 16,
  },
  listItemTitle: {
    lineHeight: 19,
  },
  optionListItem: {
    gap: LIST_GAP_HORIZONTAL,
  },
  secondaryTitleWrapper: {
    flexDirection: 'row',
    gap: 4,
  },
  skeletonHeader: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
    marginVertical: LIST_ITEM_PADDING,
  },
  tags: {
    flexDirection: 'row',
    gap: 8,
  },
  tagsWrapper: {
    marginRight: -1 * ORDER_INBOUND_LIST_ITEM_MARGIN,
    paddingRight:
      ORDER_INBOUND_LIST_ITEM_MARGIN + ORDER_INBOUND_LIST_ITEM_TIME_WIDTH,
  },
  tagsWrapperNarrow: {
    marginRight: 0,
    paddingRight: 0,
  },
  timeText: {
    lineHeight: 21,
    minWidth: ORDER_INBOUND_LIST_ITEM_TIME_WIDTH,
    textAlign: 'right',
  },
}));

export const useSharedOrderListStyles = createStyles((theme) => ({
  sectionHeader: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: LIST_MARGIN_HORIZONTAL / 2,
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
    paddingRight: LIST_MARGIN_HORIZONTAL / 2,
    paddingTop: LIST_ITEM_PADDING,
  },
  sectionHeaderFirst: {
    paddingTop: LIST_MARGIN_HORIZONTAL,
  },
  subTitle: {
    alignItems: 'center',
    fontSize: 16,
  },
}));
